import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { messaging, getToken, onMessage } from '../config/firebase';
import { logger } from '../utils/logger';

export type NotificationPermission = 'default' | 'granted' | 'denied';

export interface UsePushNotificationsReturn {
  permission: NotificationPermission;
  isSupported: boolean;
  isSubscribed: boolean;
  loading: boolean;
  error: string | null;
  fcmToken: string | null;
  requestPermission: () => Promise<boolean>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  sendTestNotification: () => Promise<void>;
}

export const usePushNotifications = (
  tenantId: string,
  customerId?: string
): UsePushNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vérifier le support des notifications
  useEffect(() => {
    const supported = !!messaging && 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission as NotificationPermission);
    }
  }, []);

  // Écouter les messages quand l'app est au premier plan
  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      logger.log('Message reçu au premier plan:', payload);

      // Mettre à jour le badge icône PWA
      if ('setAppBadge' in navigator && payload.data?.unread_count) {
        const count = parseInt(payload.data.unread_count, 10);
        if (count > 0) {
          (navigator as any).setAppBadge(count).catch(() => {});
        }
      }

      // Afficher une notification custom si nécessaire
      if (payload.notification) {
        new Notification(payload.notification.title || 'Nouvelle publication', {
          body: payload.notification.body,
          icon: payload.notification.icon || '/logo.png',
          data: payload.data,
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Effacer le badge icône quand l'app revient au premier plan
  useEffect(() => {
    const clearBadge = () => {
      if ('clearAppBadge' in navigator) {
        (navigator as any).clearAppBadge().catch(() => {});
      }
    };

    // Effacer au focus de la fenêtre (retour sur l'app)
    window.addEventListener('focus', clearBadge);
    // Effacer aussi au chargement initial
    clearBadge();

    return () => {
      window.removeEventListener('focus', clearBadge);
    };
  }, []);

  // Vérifier si déjà abonné (vérifier aussi côté serveur)
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported || !customerId || !messaging || !tenantId) return;

      try {
        const currentToken = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        });

        if (currentToken) {
          setFcmToken(currentToken);

          // Vérifier que le token est bien enregistré côté serveur
          try {
            // On envoie une requête pour vérifier/réenregistrer le token
            await api.post(
              '/subscriptions/push/subscribe',
              {
                customer_id: customerId,
                fcm_token: currentToken,
              },
              {
                headers: { 'X-Tenant-ID': tenantId },
              }
            );
            setIsSubscribed(true);
          } catch (serverErr) {
            logger.error('Token local trouvé mais non enregistré côté serveur:', serverErr);
            // Le token existe localement mais pas côté serveur
            setIsSubscribed(false);
          }
        }
      } catch (err) {
        logger.error('Erreur vérification abonnement:', err);
      }
    };

    checkSubscription();
  }, [isSupported, customerId, tenantId]);

  // Demander la permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Les notifications ne sont pas supportées par ce navigateur');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result === 'granted';
    } catch (err: any) {
      setError('Erreur lors de la demande de permission');
      logger.error('Permission error:', err);
      return false;
    }
  }, [isSupported]);

  // S'abonner aux notifications push avec Firebase
  const subscribe = useCallback(async () => {
    if (!customerId || !tenantId) {
      setError('Customer ID et Tenant ID requis');
      return;
    }

    if (!messaging) {
      setError('Firebase Messaging non initialisé. Vérifiez votre configuration.');
      return;
    }

    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        setError('Permission refusée');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Enregistrer le service worker Firebase
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      logger.log('Service Worker Firebase enregistré');

      await navigator.serviceWorker.ready;

      // Obtenir le token FCM
      const currentToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!currentToken) {
        throw new Error('Impossible d\'obtenir le token FCM');
      }

      logger.log('Token FCM obtenu:', currentToken);
      setFcmToken(currentToken);

      // Envoyer le token au backend
      await api.post(
        '/subscriptions/push/subscribe',
        {
          customer_id: customerId,
          fcm_token: currentToken,
        },
        {
          headers: { 'X-Tenant-ID': tenantId },
        }
      );

      setIsSubscribed(true);
      logger.log('Abonné aux notifications push Firebase');
    } catch (err: any) {
      logger.error('Erreur abonnement push:', err);
      setError(err.message || 'Erreur lors de l\'abonnement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [customerId, tenantId, permission, requestPermission]);

  // Se désabonner
  const unsubscribe = useCallback(async () => {
    if (!customerId || !tenantId) {
      setError('Customer ID et Tenant ID requis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Informer le backend
      await api.post(
        '/subscriptions/push/unsubscribe',
        {
          customer_id: customerId,
        },
        {
          headers: { 'X-Tenant-ID': tenantId },
        }
      );

      setIsSubscribed(false);
      setFcmToken(null);
      logger.log('Désabonné des notifications push');
    } catch (err: any) {
      logger.error('Erreur désabonnement push:', err);
      setError(err.message || 'Erreur lors du désabonnement');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [customerId, tenantId]);

  // Envoyer une notification de test
  const sendTestNotification = useCallback(async () => {
    if (!customerId || !tenantId) {
      setError('Customer ID et Tenant ID requis');
      return;
    }

    if (!isSubscribed) {
      setError('Vous devez d\'abord activer les notifications push');
      throw new Error('Vous devez d\'abord activer les notifications push');
    }

    try {
      await api.post(
        '/subscriptions/push/test',
        {
          customer_id: customerId,
        },
        {
          headers: { 'X-Tenant-ID': tenantId },
        }
      );
      logger.log('Notification de test envoyée');
    } catch (err: any) {
      logger.error('Erreur envoi notification test:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de l\'envoi de la notification test';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [customerId, tenantId, isSubscribed]);

  return {
    permission,
    isSupported,
    isSubscribed,
    fcmToken,
    loading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification,
  };
};
